import React from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { type Asset } from 'contentful';
import { ContentfulImage, LoadingAnimation, useResponsive } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { ProductBanner } from '../ProductBanner';

export const DressingProductImage = (props: DressingProductImageProps) => {
  const { product } = props;
  const { match } = useResponsive();

  const content = useContentfulContentTypeEntry<ProductDetailsPage>({
    contentType: 'productDetailsPage',
  });

  const baseUrl = content.data?.fields?.dressingDetailsImage?.fields?.file?.url;

  if (content.fetching) {
    return (
      <View style={styles.container}>
        <LoadingAnimation />
      </View>
    );
  }

  if (baseUrl) {
    return (
      <View style={styles.container}>
        <ContentfulImage
          style={styles.image}
          contentFit="cover"
          contentPosition="bottom center"
          aria-label={product?.name}
          baseUrl={baseUrl}
          options={match([{}, imageConfigSM, imageConfigLG, imageConfigXL])}
        />
      </View>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <View style={styles.container}>
      <ProductBanner imgSrc={product.asset.url} imgAriaLabel={product.name} />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.CREAM,
  },
  image: {
    flex: 1,
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const fm = 'webp';
const fit = 'fill';
const imageConfigSM = { fm, fit, w: 720 };
const imageConfigLG = { fm, fit, w: 1080 };
const imageConfigXL = { fm, fit, w: 1440 };

// ─── Types ───────────────────────────────────────────────────────────────────

type DressingProductImageProps = {
  product: {
    name?: string;
    asset: { url: string };
  };
};

type ProductDetailsPage = {
  dressingDetailsImage?: Asset;
};
